/** Client detail — tabs (profile / orders / addresses / activity). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { RejectReasonDialog } from "@/components/common/RejectReasonDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { formatDate, formatPrice } from "@/lib/utils";

export function ClientShowPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [blockOpen, setBlockOpen] = useState(false);

  const { data: client, isLoading } = useQuery({
    queryKey: ["admin-client", id],
    queryFn: async () => {
      const { data } = await api.get(`/clients/${id}`);
      return data;
    },
  });

  const { data: orders } = useQuery({
    queryKey: ["admin-client-orders", id],
    queryFn: async () => {
      const { data } = await api.get(`/orders`, {
        params: { q: client?.phone, per_page: 50 },
      });
      return data.data;
    },
    enabled: Boolean(client?.phone),
  });

  const blockMutation = useMutation({
    mutationFn: async (reason: string) => {
      await api.post(`/clients/${id}/block`, { reason });
    },
    onSuccess: () => {
      toast.success(t("clients.blocked"));
      setBlockOpen(false);
      queryClient.invalidateQueries({ queryKey: ["admin-client", id] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? t("common.toast.errorOccurred")),
  });

  const unblockMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/clients/${id}/unblock`);
    },
    onSuccess: () => {
      toast.success(t("clients.unblocked"));
      queryClient.invalidateQueries({ queryKey: ["admin-client", id] });
    },
  });

  if (isLoading || !client) return <Skeleton className="h-96" />;

  const initials = client.full_name
    ? client.full_name
        .split(" ")
        .map((p: string) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : client.phone.slice(-2);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <Avatar className="w-20 h-20">
          {client.avatar_url && <AvatarImage src={client.avatar_url} />}
          <AvatarFallback className="text-xl">{initials}</AvatarFallback>
        </Avatar>

        <div className="flex-1">
          <h1 className="text-2xl font-heading">
            {client.full_name ?? client.phone}
          </h1>
          <div className="text-muted-foreground">{client.phone}</div>
          {client.email && (
            <div className="text-sm text-muted-foreground">{client.email}</div>
          )}
          <div className="flex gap-2 mt-2">
            {client.is_blocked ? (
              <Badge variant="destructive">
                Заблокирован: {client.blocked_reason}
              </Badge>
            ) : (
              <Badge variant="success">Active</Badge>
            )}
            <Badge variant="outline">{client.language}</Badge>
          </div>
        </div>

        <div>
          {client.is_blocked ? (
            <Button
              variant="outline"
              onClick={() => unblockMutation.mutate()}
              disabled={unblockMutation.isPending}
            >
              <ShieldCheck className="w-4 h-4 mr-1" />
              Разблокировать
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={() => setBlockOpen(true)}
              disabled={blockMutation.isPending}
            >
              <Ban className="w-4 h-4 mr-1" />
              Заблокировать
            </Button>
          )}
        </div>
      </div>

      <RejectReasonDialog
        open={blockOpen}
        onClose={() => setBlockOpen(false)}
        onConfirm={async (reason) => blockMutation.mutateAsync(reason)}
        title="Заблокировать клиента"
        description={t("clients.blockReasonPrompt")}
        confirmLabel="Заблокировать"
      />

      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Заказов</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{client.orders_count}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              Всего потрачено
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {client.total_spent > 0 ? formatPrice(client.total_spent) : "—"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              Последний визит
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm">
              {client.last_seen_at ? formatDate(client.last_seen_at) : "—"}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">Профиль</TabsTrigger>
          <TabsTrigger value="orders">Заказы ({orders?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="bonuses" disabled>
            Бонусы (Phase 4)
          </TabsTrigger>
          <TabsTrigger value="activity">Активность</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-2 pt-4">
          <Field label="ID" value={String(client.id)} />
          <Field label="Public ID" value={client.public_id ?? "—"} />
          <Field label="Phone" value={client.phone} verified={client.phone_verified} />
          <Field label="Email" value={client.email ?? "—"} verified={client.email_verified} />
          <Field label={t("common.fields.registeredAt")} value={formatDate(client.created_at)} />
        </TabsContent>

        <TabsContent value="orders" className="pt-4">
          {orders && orders.length > 0 ? (
            <div className="space-y-2">
              {orders.map((o: any) => (
                <Card key={o.id}>
                  <CardContent className="pt-4 flex items-center justify-between">
                    <div>
                      <Link
                        to={`/orders/${o.public_id}`}
                        className="font-mono text-sm font-medium hover:underline"
                      >
                        {o.order_number}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(o.created_at)}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge>{o.status}</Badge>
                      <div className="text-sm font-medium mt-1">
                        {formatPrice(o.grand_total)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Заказов пока нет</p>
          )}
        </TabsContent>

        <TabsContent value="activity" className="pt-4 space-y-2">
          <Field label="Last seen" value={client.last_seen_at ? formatDate(client.last_seen_at) : "—"} />
          <Field label="Phone verified" value={client.phone_verified ? "Yes" : "No"} />
          <Field label="Email verified" value={client.email_verified ? "Yes" : "No"} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({
  label,
  value,
  verified,
}: {
  label: string;
  value: string;
  verified?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between text-sm py-1.5 border-b">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-2">
        {value}
        {verified !== undefined && (
          <Badge variant={verified ? "success" : "secondary"} className="text-xs">
            {verified ? "✓" : "?"}
          </Badge>
        )}
      </span>
    </div>
  );
}
